import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from '@material-ui/core/Button';
import CardActions from '@material-ui/core/CardActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { CentralButtonContainer } from 'pages/documents/components/DocumentCard/styled';
import { downloadFile } from 'utils/download';

import { ReduxState } from '../../../../redux/types';
import Certificate from '../../../../types/Certificate';
import api from '../../../../utils/api';
import { useDialog } from '../../../../utils/dialog';
import snackbar from '../../../../utils/snackbar';
import { IssuedDateTypography, LeftButtonContainer, RightButtonContainer, StyledCard, StyledCardContent, StyledGradeBadge, StyledIconButton } from './styled';

interface Props {
    certificate: Certificate;
    openEditDialog: () => void;
    updateData: () => void;
    hasActiveAssessment: boolean;
}

const CertificateCard = (props: Props) => {
    const { certificate, openEditDialog, updateData, hasActiveAssessment } = props;
    const { companyId, id, issueDate, grade, co2Eq, kgReducedPlastic } = certificate;

    const { t } = useTranslation();

    const userInfo = useSelector((state: ReduxState) => state.user);
    const isAdmin = userInfo.type === 'admin';

    const [openDialog, closeDialog] = useDialog();
    const [isLoading, setIsLoading] = useState(false);

    const base64toBlob = useCallback((dataURI: string) => {
        // convert a base64 string to a blob object
        const byteString = atob(dataURI);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new Blob([ab]);
    }, []);

    const download = useCallback(() => {
        // fetch the file content, decode the base64 and download it
        if (certificate.document !== null) {
            setIsLoading(true);
            api.request('/companies/' + companyId + '/documents/' + certificate.document.id).then(res => {
                const { content } = res;
                const fileName = (certificate?.document?.documentName ?? '').replace(/\W+/g, '_');
                const blob = base64toBlob(content);

                downloadFile(blob, fileName + '.' + (certificate?.document?.extension ?? ''));

                setIsLoading(false);
            }).catch(() => {
                setIsLoading(false);
            });
        }
    }, [base64toBlob, companyId, certificate]);

    const deleteCertificate = useCallback(() => {
        api.request('/companies/' + companyId + '/certificates/' + id, 'DELETE').then(() => {
            snackbar.success(t('dialogs.certificate_delete.success'));
            updateData();
        });
    }, [companyId, id, updateData, t]);

    const showDeleteDialog = useCallback(() => {
        openDialog({
            title: t('dialogs.certificate_delete.title'),
            message: (
                <>
                    {t('dialogs.certificate_delete.message')}

                    {hasActiveAssessment && certificate.assessmentId && (
                        <p style={{ marginBottom: 0 }}>
                            {t('dialogs.certificate_delete.active_assessment_notice')}
                        </p>
                    )}
                </>
            ),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteCertificate();
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteCertificate, certificate, hasActiveAssessment, openDialog, t]);

    return (
        <StyledCard>
            <StyledCardContent>
                <StyledGradeBadge grade={grade} />

                <IssuedDateTypography variant='body2' gutterBottom >
                    {t('pages.certificates.issue_date', { date: issueDate })}
                </IssuedDateTypography>

                {(kgReducedPlastic !== null || co2Eq !== null) && (
                    <IssuedDateTypography variant='body2' gutterBottom >
                        {kgReducedPlastic !== null && (
                            <>{t('pages.certificates.reduced_plastic', { value: (!kgReducedPlastic ? kgReducedPlastic : (kgReducedPlastic * -1)) })}</>
                        )}

                        {(kgReducedPlastic !== null && co2Eq !== null) && <br />}
                        {co2Eq !== null && (
                            <> {t('pages.certificates.co2_saving', { value: (!co2Eq ? co2Eq : (co2Eq * -1)) })}</>
                        )}

                    </IssuedDateTypography>
                )}
            </StyledCardContent>

            <CardActions>
                {isAdmin && (
                    <LeftButtonContainer>
                        <StyledIconButton onClick={openEditDialog} size='small' >
                            <EditIcon />
                        </StyledIconButton>
                    </LeftButtonContainer>
                )}

                {certificate.document && (
                    <CentralButtonContainer>
                        {isLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            <Button size='small' onClick={download} disabled={isLoading} >
                                {t('pages.documents.' + (certificate.document.extension === 'pdf' ? 'preview' : 'download'))}
                            </Button>
                        )}
                    </CentralButtonContainer>
                )}

                {isAdmin && (
                    <RightButtonContainer>
                        <StyledIconButton onClick={showDeleteDialog} size='small' >
                            <DeleteIcon />
                        </StyledIconButton>
                    </RightButtonContainer>
                )}
            </CardActions>
        </StyledCard>
    );
};

export default CertificateCard;
